/* eslint-disable @typescript-eslint/camelcase */
import * as React from 'react';
import './SearchPage.css';
import SearchResults from './SearchResults';
import { SearchSchoolDocument } from '../types';
import { imgSrc, backgroundImagePrefix, backgroundImagePrefixes } from '../loadBackgroundImage';
import { SunIcon, CheckedIcon, PlaneIcon } from './common/icons/Common';
import { gaEvent } from '../analytics';

const straplines = {
    mountain_biking: {
        title: 'send it',
        subtitle: 'with no return address'
    },
    kite_surfing: {
        title: 'find your edge',
        subtitle: 'and ride into the sunset'
    },
    sup_tropical: {
        title: 'sunlit reflection',
        subtitle: 'it\'s probably not a shark, right?'
    },
    climbing_view: {
        title: 'getting high',
        subtitle: 'is always better with friends'
    },
    diving: {
        title: 'hidden worlds',
        subtitle: 'a place to make new friends'
    },
    surfing: {
        title: 'surf\'s up',
        subtitle: 'wax on, paddle out, pop up'
    },
    snowboarding: {
        title: 'the lone wolf',
        subtitle: 'leaves only fresh tracks'
    },
    windsurfing: {
        title: 'ride the wave',
        subtitle: 'without all the paddling'
    }
};

export interface SearchPageProps {
    searchText: string;
    searchResults: SearchSchoolDocument[];
    onChangeSearchText: (text: string) => void;
    imgPrefix?: string;
}

export default class SearchPage extends React.Component<SearchPageProps> {
    render() {

        const strapline = straplines[backgroundImagePrefix] || {
            title: 'live outside today',
            subtitle: 'or die a little bit inside tomorrow'
        };

        return (
            <div className="pageBody searchPageBody">
                <section className="searchPageMotto">
                    <label className="labelMotto labelMottoClean">{strapline.title}</label>
                    <label className="labelMotto">{strapline.subtitle}</label>
                    <SearchResults {...this.props} />
                    <div className="searchSubtitle">
                        The new outdoor activity booking platform | <b>Book your next adventure here</b> | New destinations and activities coming soon!
                    </div>
                </section>
                <section className="flex-col flex-center searchSection">
                    <h2>How this works</h2>
                    <div className="flex-row flex-wrap flex-center">
                        <div className="flex-col flex-fill flex-center stepItem">
                            <div className="flex-row flex-center">
                                <SunIcon /><h4>&nbsp;Get inspired</h4>
                            </div>
                            <div>
                                {`Find a new destination to explore, an exciting activity to try, or that school you've heard about.`}
                            </div>
                        </div>
                        <div className="flex-col flex-fill stepItem">
                            <div className="flex-row flex-center">
                                <PlaneIcon /><h4>&nbsp;Send request</h4>
                            </div>
                            <div>
                                {`Tell us your dates, the activities you want to book, and the number of people in your group.`}
                            </div>
                        </div>
                        <div className="flex-col flex-fill stepItem">
                            <div className="flex-row flex-center">
                                <CheckedIcon /><h4>&nbsp;Confirm Booking</h4>
                            </div>
                            <div>
                                {`We'll send you offers with availability and prices. Select one which works for you, and hit confirm.`}
                            </div>
                        </div>
                    </div>
                </section>
                <section className="flex-row flex-wrap searchSection">
                    <FeaturedTile offset={1} />
                    <FeaturedTile offset={2} />
                    <FeaturedTile offset={3} />
                </section>
                <section className="flex-col flex-center searchSection">
                    <h3>Which activity should we offer next?</h3>
                    <NextActivitySuggestion activities={['Climbing', 'Mountain biking', 'Paragliding']} />
                </section>

            </div>
        );
    }
}

const FeaturedTile = ({ offset }: { offset: number }) => {
    const index = backgroundImagePrefixes.indexOf(backgroundImagePrefix);
    const nextIndex = (index + offset) % backgroundImagePrefixes.length;
    return (
        <div
            className="featuredTile"
            style={{ backgroundImage: `url('${imgSrc(backgroundImagePrefixes[nextIndex], 640)}')` }}
        />
    );
};

interface NextActivitySuggestionProps {
    activities: string[];
}

class NextActivitySuggestion extends React.Component<NextActivitySuggestionProps> {
    state = {
        selected: ''
    };

    onClick(activity: string) {
        gaEvent({ category: 'ActivitySuggestion', action: activity });
        this.setState({ selected: activity === this.state.selected ? '' : activity });
    }

    render() {
        return (
            <div className="flex-col flex-center">
                <div className="flex-row flex-center flex-wrap nextActivitySuggestionRow">
                    {
                        this.props.activities.map(a =>
                            <NextActivitySuggestionItem isSelected={a === this.state.selected} key={a.toLowerCase()} activity={a} onClick={() => this.onClick(a)} />
                        )
                    }
                </div>
                {
                    <div className="flex-row flex-center" style={{ height: '24px' }}>
                        {
                            this.state.selected
                                ? <><CheckedIcon />&nbsp;{`We're on it, thanks for letting us know!`}</>
                                : null
                        }
                    </div>}
            </div>
        );
    }
}

const NextActivitySuggestionItem = ({ activity, isSelected, onClick }: { activity: string; isSelected: boolean; onClick: () => void }) => {
    const cn = `nextActivitySuggestion ${isSelected ? 'nextActivitySuggestionSelected' : ''}`;
    return <button className={cn} onClick={onClick}>{activity}</button>;
};
